import { IFormHandler } from "client/Forms";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { FormData, FormDataData } from "./ShareALinkDialog";
import styles from "./AdvancedLinkConfigurationSection.module.less";
import { LinkIcon } from "./LinkIcon";
import { localize } from "client_legacy/ndaw/culture";
import classNames from "classnames";
import {
	Checkbox,
	DatePicker,
	Form,
	FormItem,
	Icon,
	MultiComboBox,
	MultiComboBoxItem,
	MultiComboBoxSuggestionsResult,
	RadioButtonGroup,
	RadioButtonGroupItem,
	SuggestedMultiComboBoxItem,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	findMembers,
	SharePointPeoplePickerItemModel
} from "client/Api/Documents/SharePoint/sharePointSharing";
import { getHighlight } from "client/Misc/highlight";
import { ListLocationViewModel } from "client/Api/Documents/SharePoint/common";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { delay, setTrackingId } from "client/Misc/utils";
import { LinkAudienceKind } from "./common";
import { TimeZone } from "client/DateAndTime/timezones";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

export type AdvancedLinkConfigurationSectionProps = {
	formHandler: IFormHandler<FormData>;
	areAnonymousLinksAllowed: boolean;
	isShareByEmailEnabled: boolean;
	location: ListLocationViewModel;
	timezone: TimeZone;
};

export const AdvancedLinkConfigurationSection = (props: AdvancedLinkConfigurationSectionProps): ReactElement => {
	const {
		formHandler,
		areAnonymousLinksAllowed,
		isShareByEmailEnabled,
		location,
		timezone
	} = props;

	const audienceAttachable: IValueInputAttachable<LinkAudienceKind> = formHandler.for("audience");
	const audience: LinkAudienceKind = audienceAttachable.value;
	const dataAttachable: IValueInputAttachable<FormDataData> = formHandler.for("data");

	return (
		<div>
			<Form onSubmit="NONE">
				<FormItem
					label={localize("_WhoWillHaveAccess")}
				>
					<ul className={styles.audiences}>
						<li>
							<LinkAudience
								kind={LinkAudienceKind.Anonymous}
								isAvailable={areAnonymousLinksAllowed}
								attachable={audienceAttachable}
							/>
						</li>

						<li>
							<LinkAudience
								kind={LinkAudienceKind.Organization}
								isAvailable
								attachable={audienceAttachable}
							/>
						</li>

						<li>
							<LinkAudience
								kind={LinkAudienceKind.PeopleWithExistingAccess}
								isAvailable
								attachable={audienceAttachable}
							/>
						</li>

						<li>
							<LinkAudience
								kind={LinkAudienceKind.SpecificPeople}
								isAvailable
								attachable={audienceAttachable}
							/>
						</li>
					</ul>
				</FormItem>

				{audience === LinkAudienceKind.SpecificPeople && (
					<FormItem
						label={localize("_SpecifyPeople")}
						isRequired
					>
						<SelectPeople
							attachable={dataAttachable.for("selectedPeople")}
							isShareByEmailEnabled={isShareByEmailEnabled}
							location={location}
						/>

						<ValidationError
							{...dataAttachable.for("selectedPeople").attachToValidationError()}
						/>
					</FormItem>
				)}

				{audience !== LinkAudienceKind.PeopleWithExistingAccess && (
					<FormItem
						label={localize("_Permissions")}
						isForGroup
					>
						<RadioButtonGroup
							{...dataAttachable.for("allowEditing").attachToRadioButtonGroup()}
							options={permissionOptions}
						/>

						<ValidationError
							{...dataAttachable.for("allowEditing").attachToValidationError()}
						/>
					</FormItem>
				)}

				{audience === LinkAudienceKind.Anonymous && (
					<>
						<FormItem
							label={localize("_OtherSettings")}
						>
							<Checkbox
								{...dataAttachable.for("expirationDate")
									.for("isSet")
									.attachToCheckbox()
								}
								text={localize("_SetExpirationDate")}
							/>
						</FormItem>

						{dataAttachable.for("expirationDate")
							.when(
								x => x.isSet,
								(handler) => (
									<FormItem>
										<DatePicker
											{...handler.for("value").attachToDatePicker()}
											mode="date"
											timezone={timezone}
										/>

										<ValidationError
											{...handler.for("value").attachToValidationError()}
										/>
									</FormItem>
								)
						)}
					</>
				)}
			</Form>
		</div>
	);
};
AdvancedLinkConfigurationSection.displayName = "AdvancedLinkConfigurationSection";

const permissionOptions: readonly RadioButtonGroupItem<boolean>[] = [
	{
		text: localize("_CanEdit"),
		value: true
	},
	{
		text: localize("_CanView"),
		value: false
	}
];

type LinkAudienceProps = {
	kind: LinkAudienceKind;
	attachable: IValueInputAttachable<LinkAudienceKind>;
	isAvailable: boolean;
};

const LinkAudience = (props: LinkAudienceProps): ReactElement => {
	const {
		kind,
		isAvailable,
		attachable
	} = props;

	const isSelected: boolean = attachable.value === kind;

	const clickHandler = useCallback(
		(): void => {
			if (isSelected) {
				return;
			}

			attachable.reducer
				.setValue(kind);

			attachable.validation
				.triggerInputFinished();
		},
		[
			attachable.reducer,
			attachable.validation,
			isSelected,
			kind
		]
	);

	return (
		<div
			{...setTrackingId(attachable.reducer.path, `|${kind}`)}
			className={classNames(
				styles.linkAudience,
				{
					[styles.linkAudience_disabled]: !isAvailable,
					[styles.linkAudience_selected]: isSelected
				}
			)}
			title={
				isAvailable
					? undefined
					: localize("_YourOrganisationIsPreventingYouFromSelectingThisOption")
			}
			onClick={
				isAvailable
					? clickHandler
					: undefined
			}
		>
			<LinkIcon
				kind={kind}
			/>
			<p className={styles.linkAudienceDescription}>
				{localize(audienceToLocalizationKeyMap[kind])}
			</p>

			{isSelected && (
				<Icon
					value={{
						kind: "font",
						name: "check",
						color: "primary"
					}}
				/>
			)}
		</div>
	);
};
LinkAudience.displayName = "LinkAudience";

type SelectPeopleProps = {
	attachable: IValueInputAttachable<readonly MultiComboBoxItem<SharePointPeoplePickerItemModel>[]>;
	isShareByEmailEnabled: boolean;
	location: ListLocationViewModel;
};

const SelectPeople = (props: SelectPeopleProps): ReactElement => {
	const {
		attachable,
		isShareByEmailEnabled,
		location
	} = props;

	const [suggesionsResult, setSuggesionsResult] = useState<MultiComboBoxSuggestionsResult<SharePointPeoplePickerItemModel>>({
		loadingStatus: "loading"
	});

	const findPeopleHandler = useCallback(
		async (searchTerm: string, signal: AbortSignal): Promise<void> => {
			setSuggesionsResult({
				loadingStatus: "loading"
			});

			try {
				await delay(400, signal);

				const members: readonly SharePointPeoplePickerItemModel[] = await findMembers(
					convertToLocationModel(location),
					searchTerm,
					isShareByEmailEnabled,
					signal
				);

				setSuggesionsResult({
					loadingStatus: "ready",
					suggestions: members.map(
						(x): SuggestedMultiComboBoxItem<SharePointPeoplePickerItemModel> => ({
							text: x.name,
							value: x,
							highlight: getHighlight(x.name, searchTerm),
							valueTrackingId: x.memberKey
						})
					)
				});
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				setSuggesionsResult({ loadingStatus: "error" });
				console.error(e);
			}
		},
		[
			isShareByEmailEnabled,
			location
		]
	);

	return (
		<MultiComboBox
			{...attachable.attachToMultiComboBox()}
			onFind={findPeopleHandler}
			renderSuggestionBox={
				(props, ref) => (
					<MultiComboBox.SuggestionBox
						baseProps={props}
						ref={ref}
						suggestionsResult={suggesionsResult}
						menuTitle={localize("_SpecifyPeople")}
					/>
				)
			}
		/>
	);
};
SelectPeople.displayName = "SelectPeople";

const audienceToLocalizationKeyMap: Record<LinkAudienceKind, LocalizationKey> = {
	[LinkAudienceKind.Anonymous]: "_AnyoneWithTheLink",
	[LinkAudienceKind.Organization]: "_PeopleInThisOrganisationWithTheLink",
	[LinkAudienceKind.SpecificPeople]: "_SpecificPeople",
	[LinkAudienceKind.PeopleWithExistingAccess]: "_OnlyPeopleWithExistingAccess"
};
